import { useState, useEffect } from 'react';

interface ExitIntentPopupProps {
  onClose?: () => void;
  onEmailSubmit?: (email: string) => void;
}

export const ExitIntentPopup = ({ onClose, onEmailSubmit }: ExitIntentPopupProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const hasShown = sessionStorage.getItem('exitIntentShown');
    if (hasShown) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Detect mouse moving to top of viewport (likely to close tab)
      if (e.clientY <= 10 && !isVisible) {
        setIsVisible(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setIsSubmitted(true);
      onEmailSubmit?.(email);

      // Close popup after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-in zoom-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close popup"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>

        {!isSubmitted ? (
          <>
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-coral-100 to-teal-100 rounded-full p-4">
                <svg
                  className="w-12 h-12 text-coral-700"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
            </div>

            {/* Content */}
            <h2
              id="exit-intent-title"
              className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-coral-700 to-teal-700 bg-clip-text text-transparent"
            >
              Before You Go...
            </h2>
            <p className="text-center text-gray-600 mb-6 text-lg">
              Get our <span className="font-bold text-coral-800">free safety guide</span> and learn how to protect your loved ones
            </p>

            {/* Email form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="exit-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="exit-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-coral-400 focus:border-transparent outline-none transition-all text-lg"
                  aria-required="true"
                />
              </div>
              <button
                type="submit"
                className="w-full btn-primary text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-all transform hover:scale-105 text-lg"
              >
                Get Free Guide
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-4">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-teal-100 to-coral-100 rounded-full p-4">
                <svg
                  className="w-12 h-12 text-teal-600"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
            <p className="text-gray-600 text-lg">
              Check your inbox for your free safety guide
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitIntentPopup;
